import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

function GuideLink({ to, children }) {
    const history = useHistory();

    function handleClick() {
        const el = document.getElementById('modalGuide');
        if (el && window.bootstrap) {
            const instance = window.bootstrap.Modal.getInstance(el);
            if (instance) instance.hide();
        }
        history.push(to);
    }

    return (
        <button type='button' className='btn btn-sm btn-outline-primary mt-1' onClick={handleClick}>
            {children}
        </button>
    );
}

// ─── Tab 1: Setup Inicial ────────────────────────────────────────────────

const SETUP_SECTIONS = [
    {
        id: 'overview',
        title: 'O que é este sistema?',
        renderContent: () => (
            <div>
                <p>Este sistema é um <strong>bot de trading automatizado</strong> que monitora o mercado de criptomoedas e executa ordens na sua corretora conforme regras que você configura.</p>
                <p>O fluxo básico é:</p>
                <ol>
                    <li><strong>Monitor</strong> — coleta dados do mercado (preços, indicadores técnicos, regime)</li>
                    <li><strong>Automação</strong> — avalia condições e dispara ações quando satisfeitas</li>
                    <li><strong>Estratégia</strong> — agrupa monitor + automações em um único ativável</li>
                    <li><strong>IA</strong> (opcional) — analisa o mercado e gera sinais de compra/venda</li>
                </ol>
                <p className='text-muted small mb-0'>Você pode operar manualmente via "Ordens" ou deixar o sistema agir automaticamente pelas automações.</p>
            </div>
        )
    },
    {
        id: 'exchange',
        title: 'Passo 1 — Configurar Corretora',
        renderContent: () => (
            <div>
                <p>Conecte sua conta da corretora para que o sistema possa consultar saldos e executar ordens.</p>
                <ol>
                    <li>Acesse <strong>Configurações → Corretora</strong></li>
                    <li>Selecione sua corretora (Binance, Bybit, OKX ou Bitget)</li>
                    <li>Gere uma chave de API na corretora com permissão de leitura e negociação</li>
                    <li>Informe a <em>Access Key</em> e a <em>Secret Key</em> nas configurações</li>
                    <li>Clique em <strong>Testar</strong> para validar a conexão</li>
                </ol>
                <p className='text-warning small mb-1'>Nunca compartilhe suas chaves de API. Use permissões mínimas (leitura + negociação spot, sem saque). Use sub-account dedicada — veja as boas práticas de segurança em Configurações → Corretora.</p>
                <GuideLink to='/settings'>Ir para Configurações →</GuideLink>
            </div>
        )
    },
    {
        id: 'ai',
        title: 'Passo 2 — Configurar IA (opcional)',
        renderContent: () => (
            <div>
                <p>O sistema suporta análise automática via IA (Claude, GPT, Groq, Gemini e outros). A IA analisa dados de mercado e gera sinais de compra/venda que podem ser usados pelas automações.</p>
                <ol>
                    <li>Acesse <strong>Configurações → IA</strong></li>
                    <li>Adicione um provedor (recomendamos começar com Groq ou Gemini — têm planos grátis)</li>
                    <li>Cole a API Key do provedor</li>
                    <li>Você pode adicionar VÁRIOS provedores. O <strong>primary</strong> é usado por padrão; os outros viram <strong>fallback</strong> automático quando o primary falha ou estoura limite.</li>
                    <li>Clique em <strong>Testar</strong> para confirmar que a chave funciona</li>
                </ol>
                <p className='text-muted small mb-1'>Sem IA configurada você ainda pode operar com indicadores técnicos (EMA, RSI, etc.).</p>
                <GuideLink to='/settings'>Ir para Configurações →</GuideLink>
            </div>
        )
    },
    {
        id: 'monitors',
        title: 'Passo 3 — Monitores',
        renderContent: () => (
            <div>
                <p><strong>Monitores</strong> são os "olhos" do sistema. Eles coletam dados do mercado em tempo real.</p>
                <ul>
                    <li><strong>Candles</strong> — calcula indicadores técnicos (EMA, RSI, MACD...) para um ativo e intervalo</li>
                    <li><strong>Regime de Mercado</strong> — classifica o mercado como Tendência de Alta, Baixa, Lateral ou Alta Volatilidade</li>
                    <li><strong>Scanner de Mercado</strong> — varre múltiplos ativos identificando oportunidades</li>
                    <li><strong>Funding Rate</strong> — monitora taxas de financiamento de contratos perpétuos</li>
                </ul>
                <p className='text-warning small mb-1'>Sem monitores ativos as automações não recebem dados e não disparam.</p>
                <GuideLink to='/monitors'>Ir para Monitores →</GuideLink>
            </div>
        )
    },
    {
        id: 'automations',
        title: 'Passo 4 — Automações',
        renderContent: () => (
            <div>
                <p><strong>Automações</strong> definem regras: "quando X acontecer, faça Y".</p>
                <ul>
                    <li><strong>Regular</strong> — reage a eventos de mercado (ex: EMA 9 cruzou acima da EMA 21)</li>
                    <li><strong>Agendada</strong> — executa em horário fixo (ex: "comprar às 9h todo dia útil")</li>
                    <li><strong>Grid</strong> — cria grade de ordens em faixas de preço para aproveitar oscilações</li>
                </ul>
                <p>Cada automação tem <strong>condições</strong> (expressões que verificam valores do mercado) e <strong>ações</strong> (o que fazer quando verdadeiras).</p>
                <p className='text-muted small mb-1'><strong>Dica</strong>: pra começar manual, use a ação <strong>🤖 Alerta IA</strong> em vez de <strong>Enviar Ordem</strong>. Você recebe notificação quando a IA decidiria comprar/vender, mas a ordem só sai se você decidir manualmente.</p>
                <GuideLink to='/automations'>Ir para Automações →</GuideLink>
            </div>
        )
    },
    {
        id: 'strategies',
        title: 'Passo 5 — Estratégias',
        renderContent: () => (
            <div>
                <p>Uma <strong>Estratégia</strong> agrupa monitor + automação de compra + automação de venda em um único componente ativável com um clique.</p>
                <p><strong>Forma mais rápida de começar:</strong> use os <strong>✨ Templates</strong> na aba de Estratégias:</p>
                <ul>
                    <li><strong>AI Trader</strong> — a IA analisa o mercado a cada 15 min e decide compra/venda</li>
                    <li><strong>EMA Crossover</strong> — estratégia clássica de cruzamento de médias móveis</li>
                    <li><strong>DCA com Filtro de Regime</strong> — acumula em tendência de alta, para em queda</li>
                </ul>
                <GuideLink to='/strategies'>Ver Templates de Estratégia →</GuideLink>
            </div>
        )
    },
    {
        id: 'orders',
        title: 'Passo 6 — Primeira Ordem',
        renderContent: () => (
            <div>
                <p>Para executar uma ordem <strong>manualmente</strong>:</p>
                <ol>
                    <li>Acesse <strong>Ordens</strong> no menu lateral</li>
                    <li>Escolha o símbolo (ex: BTCUSDT), tipo e quantidade</li>
                    <li>Confirme a ordem</li>
                </ol>
                <p><strong>Atalho prático</strong>: no Dashboard, quando aparecer "Pronto pra entrar" na coluna Status do Cockpit, clique em <strong>🟢 Comprar agora →</strong> — abre New Order pré-preenchido com a sugestão da IA (stop loss, take profit, preço).</p>
                <p>Para ordens <strong>automáticas</strong>: crie um Template de Ordem e associe-o a uma automação como ação "Enviar Ordem".</p>
                <GuideLink to='/orders'>Ir para Ordens →</GuideLink>
            </div>
        )
    },
    {
        id: 'refinement',
        title: 'Passo 7 — Refinamento',
        renderContent: () => (
            <div>
                <p><strong>Manual:</strong></p>
                <ul>
                    <li>Use <strong>Backtest</strong> para testar a estratégia com dados históricos antes de ativar</li>
                    <li>Ajuste parâmetros dos indicadores (períodos de EMA, limiar do RSI)</li>
                    <li>Adicione filtros de regime para evitar operar em mercados desfavoráveis</li>
                    <li>Configure stop-loss e take-profit nos templates de ordem</li>
                </ul>
                <p><strong>Com IA:</strong></p>
                <ul>
                    <li>Use o template <strong>AI Trader</strong> para análise automática a cada 15 min</li>
                    <li>Combine IA + filtro de regime para aumentar a precisão</li>
                    <li>Monitore o consumo de tokens em Configurações → IA</li>
                </ul>
                <p className='mb-0 text-muted small'>Dica: comece em modo <strong>Teste</strong> (testnet) para validar sem dinheiro real antes de promover pra Produção.</p>
            </div>
        )
    }
];

// ─── Tab 2: Como Operar (manual operacional) ─────────────────────────────

const OPERATION_SECTIONS = [
    {
        id: 'workflow',
        title: '📅 Workflow recomendado (primeiros 30 dias)',
        renderContent: () => (
            <div>
                <p>Pra começar sem se queimar:</p>
                <p><strong>Semana 1 — Observar sem operar</strong></p>
                <ul>
                    <li>Configure exchange e IA (passos da aba "Início Rápido")</li>
                    <li>Adicione 3-5 ativos favoritos em <strong>Símbolos</strong></li>
                    <li>Crie automação tipo <strong>🤖 Alerta IA</strong> nesses ativos (sem disparar ordem)</li>
                    <li>Olhe o Dashboard 2x por dia. Receba alertas via Telegram</li>
                    <li><strong>Não opere nada</strong> — apenas observe se os sinais fazem sentido</li>
                </ul>
                <p><strong>Semana 2 — Comparar provedores de IA</strong></p>
                <ul>
                    <li>Adicione um segundo provedor (ex: Claude como primary, Gemini como fallback)</li>
                    <li>Veja <strong>Performance → Acurácia por IA</strong> — qual está acertando mais</li>
                    <li>Use o filtro de confiança pra ver onde a IA é mais decisiva</li>
                </ul>
                <p><strong>Semana 3 — Operar em testnet</strong></p>
                <ul>
                    <li>Configure credenciais de testnet em <strong>Configurações → Corretora</strong></li>
                    <li>Crie automações marcadas como <strong>🟡 Teste</strong></li>
                    <li>Deixa rodar 7 dias. Observe Win Rate, Drawdown, Acurácia</li>
                </ul>
                <p><strong>Semana 4+ — Produção com capital pequeno</strong></p>
                <ul>
                    <li>Quando confortável, promova automação pra Produção (clique em <strong>🟢 Promover</strong>)</li>
                    <li>Use sub-account com capital limitado (ver boas práticas em Settings)</li>
                    <li>Reavalie a cada 2 semanas — não fica mexendo todo dia</li>
                </ul>
            </div>
        )
    },
    {
        id: 'dashboard-reading',
        title: '🔍 Como ler o Dashboard',
        renderContent: () => (
            <div>
                <p>O Dashboard é seu painel diário. Da cima pra baixo:</p>
                <p><strong>1. Health Banner</strong> (🟢/🟡/🔴 no topo)</p>
                <ul>
                    <li>🟢 = operação saudável, segue rodando</li>
                    <li>🟡 = atenção, alguma métrica em zona de alerta</li>
                    <li>🔴 = crítico, vai pro Performance e investigue</li>
                </ul>
                <p><strong>2. Pulso do Mercado</strong> (widget com 3 colunas)</p>
                <ul>
                    <li>🔥 <strong>Em Alta</strong>: ativos que a IA recomenda focar agora</li>
                    <li>🌱 <strong>Novos Candidatos</strong>: ativos que merecem começar a monitorar</li>
                    <li>📉 <strong>Saindo do Foco</strong>: estavam bem, perderam força</li>
                    <li>Atualiza a cada 4h — não precisa olhar de minuto em minuto</li>
                    <li>Tooltip em cada chip mostra o raciocínio agregado</li>
                </ul>
                <p><strong>3. Favoritos · Cockpit</strong> (tabela de status por símbolo)</p>
                <ul>
                    <li><strong>Regime</strong>: estado do mercado (📈 Alta / 📉 Baixa / ↔️ Lateral / ⚡ Volátil)</li>
                    <li><strong>Sinal IA</strong>: BUY / SELL / HOLD com confiança</li>
                    <li><strong>Status</strong>: o que o bot está fazendo (esperando, em posição, etc.)</li>
                    <li>Quando ver "Pronto pra entrar", clique em <strong>🟢 Comprar agora →</strong></li>
                    <li>Ícone 📜 abre últimos 5 sinais da IA pra aquele símbolo</li>
                </ul>
                <p><strong>4. Posições Abertas</strong> (P&L em tempo real)</p>
                <p className='text-muted small'>Mostra quanto cada posição está rendendo agora. Verde = ganhando, vermelho = perdendo.</p>
            </div>
        )
    },
    {
        id: 'performance-reading',
        title: '📊 Como ler o Performance',
        renderContent: () => (
            <div>
                <p>Vá em <strong>Inteligência → Performance</strong> pra entender se a estratégia tá funcionando.</p>
                <p><strong>Headline Metrics</strong> (5 cards no topo)</p>
                <ul>
                    <li><strong>Win Rate</strong>: % de trades com lucro. Verde &gt;60%, amarelo 40-60, vermelho &lt;40</li>
                    <li><strong>Profit Factor</strong>: lucro bruto ÷ prejuízo bruto. Saudável quando &gt; 1.5</li>
                    <li><strong>Max Drawdown</strong>: pior queda de pico do P&L acumulado</li>
                    <li><strong>Win/Loss</strong>: relação do ticket médio (quanto vale um ganho médio vs uma perda média)</li>
                    <li><strong>Trades/dia</strong>: ritmo da operação. Acima de 5 é over-trading</li>
                </ul>
                <p><strong>Equity Curve</strong> (gráfico)</p>
                <ul>
                    <li>Mostra evolução do P&L acumulado</li>
                    <li>Bolinhas vermelhas = trades perdedoras, cinzas = ganhadoras</li>
                    <li><strong>Clique numa bolinha vermelha</strong> pra abrir post-mortem da trade</li>
                </ul>
                <p><strong>Post-mortem</strong> (abre ao clicar no gráfico)</p>
                <ul>
                    <li>Mostra ordem + sinal IA do momento + regime + automação que disparou</li>
                    <li>Útil pra entender por que uma perda específica aconteceu</li>
                    <li>Se for sinal IA errado, é azar. Se for setup mal configurado, ajusta.</li>
                </ul>
                <p><strong>Acurácia por IA</strong> (tabela mais abaixo)</p>
                <ul>
                    <li>Compara provedores configurados (Claude vs Groq vs DeepSeek...)</li>
                    <li>Filtra por símbolo ou confidence mínima</li>
                    <li>Use pra decidir trocar primary se outro provider tá acertando mais consistentemente</li>
                </ul>
            </div>
        )
    },
    {
        id: 'glossary',
        title: '📚 Glossário (termos técnicos)',
        renderContent: () => (
            <div>
                <p><strong>Regimes de mercado</strong> (visíveis no Cockpit)</p>
                <ul>
                    <li><strong>TRENDING_UP</strong> 📈 — alta consistente, momento de comprar/manter</li>
                    <li><strong>TRENDING_DOWN</strong> 📉 — queda consistente, momento de esperar/sair</li>
                    <li><strong>RANGING</strong> ↔️ — lateral, oscila sem direção, bom pra grid</li>
                    <li><strong>HIGH_VOLATILITY</strong> ⚡ — oscilação grande sem rumo, arriscado</li>
                </ul>
                <p><strong>Indicadores técnicos</strong></p>
                <ul>
                    <li><strong>EMA</strong> (média móvel exponencial) — preço médio recente. Cruzamento de EMAs sinaliza mudança de tendência.</li>
                    <li><strong>RSI</strong> (0-100) — &gt;70 sobrecomprado, &lt;30 sobrevendido</li>
                    <li><strong>ATR</strong> — quanto o preço oscila tipicamente. Usado pra dimensionar stop.</li>
                    <li><strong>ADX</strong> (0-100) — força da tendência. &gt;25 tendência forte, &lt;20 mercado lateral.</li>
                    <li><strong>MACD</strong> — sinal de mudança de momentum.</li>
                </ul>
                <p><strong>Métricas de performance</strong></p>
                <ul>
                    <li><strong>Win Rate</strong> — % de trades positivas. Não conta tudo: 40% de win rate pode ser lucrativo se Win/Loss for alto.</li>
                    <li><strong>Profit Factor</strong> — soma de ganhos ÷ soma de perdas. &gt;1 lucrativo, &gt;1.5 saudável, &gt;2 excelente.</li>
                    <li><strong>Drawdown</strong> — queda do pico ao vale do patrimônio. Quanto menor, melhor.</li>
                    <li><strong>Sharpe</strong> — retorno ajustado ao risco. &gt;1 bom, &gt;2 muito bom.</li>
                </ul>
                <p><strong>Ordens</strong></p>
                <ul>
                    <li><strong>Stop Loss</strong> — preço que fecha posição em prejuízo limitado</li>
                    <li><strong>Take Profit</strong> — preço que fecha posição em lucro alvo</li>
                    <li><strong>Trailing Stop</strong> — stop loss que sobe com o preço, protegendo lucro</li>
                    <li><strong>Slippage</strong> — diferença entre preço esperado e executado</li>
                </ul>
            </div>
        )
    },
    {
        id: 'modes',
        title: '🎛️ Modos de operação',
        renderContent: () => (
            <div>
                <p>O bot suporta diferentes modos, do mais conservador ao mais arriscado:</p>
                <p><strong>1. Modo Observação (alerta sem ordem)</strong></p>
                <ul>
                    <li>Criar automação com ação <strong>🤖 Alerta IA</strong></li>
                    <li>Bot apenas notifica quando IA recomenda compra/venda</li>
                    <li>Você decide manualmente — sem risco financeiro automático</li>
                    <li><strong>Use no início, semanas 1-2</strong></li>
                </ul>
                <p><strong>2. Modo Teste (testnet)</strong></p>
                <ul>
                    <li>Automação marcada como <strong>🟡 Teste</strong></li>
                    <li>Bot opera REAL contra testnet da exchange (sem dinheiro real)</li>
                    <li>Permite validar setup completo com risco zero</li>
                    <li><strong>Use na semana 3, depois de "observação"</strong></li>
                </ul>
                <p><strong>3. Modo Produção (dinheiro real)</strong></p>
                <ul>
                    <li>Automação marcada como <strong>🟢 Produção</strong></li>
                    <li>Comece com capital pequeno em sub-account isolada</li>
                    <li><strong>Use só depois de testnet validar performance</strong></li>
                </ul>
                <p><strong>Caminho da promoção:</strong></p>
                <p className='text-muted small'>Automação de teste → botão "Promover pra produção" duplica como produção (com prefixo [Prod]) e desativa a versão de teste. Você pode reativar a de teste quando quiser.</p>
            </div>
        )
    },
    {
        id: 'decisions',
        title: '🤔 Decisões comuns com os dados',
        renderContent: () => (
            <div>
                <p>Cenários que vão acontecer e o que fazer:</p>
                <p><strong>"Win rate caiu de 60% pra 40% nos últimos 7 dias"</strong></p>
                <ul>
                    <li>Vai em Performance, filtra por símbolo. É geral ou em ativo específico?</li>
                    <li>Se for específico: revisa setup daquela automação. Talvez o regime mudou.</li>
                    <li>Se for geral: considere pausar automações por uns dias. Pode ser mercado adverso ao seu setup.</li>
                </ul>
                <p><strong>"BusinessHealthBanner virou 🔴 vermelho"</strong></p>
                <ul>
                    <li>Não entre em pânico. Clique no banner pra ir ao Performance.</li>
                    <li>Veja qual KPI desencadeou — Win rate, Profit Factor ou Drawdown.</li>
                    <li>Decida: pausa, ajusta ou aceita (se está dentro do esperado).</li>
                </ul>
                <p><strong>"Bot fez uma perda que parece estranha"</strong></p>
                <ul>
                    <li>Vá no EquityCurve, clique no ponto vermelho daquela trade.</li>
                    <li>Post-mortem mostra: ordem + sinal IA + regime + automação.</li>
                    <li>Se a IA deu confidence baixa (&lt;50%) e mesmo assim disparou, revisa threshold da condição.</li>
                    <li>Se a IA acertou direção mas o stop foi cedo demais, reveja regimeSizing/stopMultiplier.</li>
                </ul>
                <p><strong>"Provider X tá acertando mais que Y"</strong></p>
                <ul>
                    <li>Vá em Performance → Acurácia por IA</li>
                    <li>Se X tem &gt;50 sinais e acurácia &gt;65% e Y tem similar mas &lt;55%, considere promover X.</li>
                    <li><strong>Cuidado</strong>: X com 80% em 5 sinais é menos confiável que Y com 65% em 50.</li>
                </ul>
                <p><strong>"Quero parar tudo agora"</strong></p>
                <ul>
                    <li>Acesse Automações, pare todas (botão Stop em cada linha).</li>
                    <li>Posições abertas continuam — venda manualmente em Ordens se quiser fechar.</li>
                    <li>Bot não toma decisão sozinho quando automações estão paradas.</li>
                </ul>
            </div>
        )
    },
    {
        id: 'reports',
        title: '📥 Relatório exportável',
        renderContent: () => (
            <div>
                <p>Em <strong>Performance</strong>, botão <strong>📥 Exportar Relatório</strong> gera arquivo <code>.md</code> (texto) com:</p>
                <ul>
                    <li>Headline metrics do período</li>
                    <li>Equity curve em texto</li>
                    <li>Top 5 ganhos e top 5 perdas com contexto</li>
                    <li>Acurácia por IA</li>
                    <li>Performance por automação e regime</li>
                </ul>
                <p>O arquivo é <strong>auto-suficiente</strong> — dá pra colar diretamente num chat de IA pra análise externa, ou guardar como registro mensal.</p>
                <p className='text-muted small mb-0'>Útil pra quem tem pouco tempo de acompanhamento minucioso — exporta semanalmente, lê com calma depois.</p>
            </div>
        )
    },
    {
        id: 'risks',
        title: '⚠️ Riscos e disclaimers',
        renderContent: () => (
            <div>
                <p className='text-danger'><strong>Leia antes de operar dinheiro real.</strong></p>
                <ul>
                    <li><strong>Cripto é volátil</strong>. Quedas de 20%+ em poucos dias são normais. Bot não muda isso.</li>
                    <li><strong>Risco de perda total</strong>. API key + bug do bot + mercado adverso = drenagem da conta operada.</li>
                    <li><strong>Use só capital que pode perder</strong>. Não opere com reserva de emergência, dinheiro do aluguel, etc.</li>
                    <li><strong>Sub-account isolada é mandatória</strong>. Veja boas práticas em Configurações → Corretora.</li>
                    <li><strong>IA pode errar consistentemente</strong>. Acurácia de 60% significa errar 40% das vezes. Stop loss é defesa principal.</li>
                    <li><strong>Não é aconselhamento financeiro</strong>. Este sistema executa o que você configura — você é responsável pelas decisões.</li>
                </ul>
                <p><strong>Boas práticas defensivas:</strong></p>
                <ul>
                    <li>Sub-account com capital limitado ao que está disposto a perder</li>
                    <li>API key trade-only (sem permissão de saque)</li>
                    <li>IP whitelist na API key</li>
                    <li>Stop loss sempre configurado</li>
                    <li>Drawdown limits ativos em Configurações → Risco</li>
                    <li>Validar em testnet antes de produção</li>
                </ul>
                <p className='text-muted small mb-0'>O sistema é uma ferramenta. Disciplina e gestão de risco são suas.</p>
            </div>
        )
    }
];

const TABS = [
    { id: 'setup',     label: '🚀 Início Rápido', sections: SETUP_SECTIONS,     defaultOpen: 'overview' },
    { id: 'operation', label: '🎯 Como Operar',   sections: OPERATION_SECTIONS, defaultOpen: 'workflow' }
];

function OnboardingGuideModal() {
    const [activeTab, setActiveTab] = useState(TABS[0].id);
    const [openSection, setOpenSection] = useState(TABS[0].defaultOpen);

    function switchTab(tabId) {
        const tab = TABS.find(t => t.id === tabId);
        if (!tab) return;
        setActiveTab(tabId);
        setOpenSection(tab.defaultOpen);
    }

    function toggleSection(id) {
        setOpenSection(prev => prev === id ? null : id);
    }

    const currentTab = TABS.find(t => t.id === activeTab) || TABS[0];

    return (
        <div className='modal fade' id='modalGuide' tabIndex='-1' role='dialog' aria-labelledby='modalGuideLabel' aria-hidden='true'>
            <div className='modal-dialog modal-lg modal-dialog-scrollable' role='document'>
                <div className='modal-content'>
                    <div className='modal-header'>
                        <h5 className='modal-title' id='modalGuideLabel'>Ajuda / Guia do Usuário</h5>
                        <button type='button' className='btn-close' data-bs-dismiss='modal' aria-label='Fechar' />
                    </div>

                    {/* Tabs */}
                    <ul className='nav nav-tabs px-3 pt-2'>
                        {TABS.map(tab => (
                            <li key={tab.id} className='nav-item'>
                                <button
                                    type='button'
                                    className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => switchTab(tab.id)}>
                                    {tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className='modal-body p-0'>
                        <div className='accordion accordion-flush' id='accordionGuide'>
                            {currentTab.sections.map(section => (
                                <div className='accordion-item' key={section.id}>
                                    <h2 className='accordion-header'>
                                        <button
                                            className={`accordion-button ${openSection === section.id ? '' : 'collapsed'} small fw-semibold`}
                                            type='button'
                                            onClick={() => toggleSection(section.id)}
                                        >
                                            {section.title}
                                        </button>
                                    </h2>
                                    <div className={`accordion-collapse collapse ${openSection === section.id ? 'show' : ''}`}>
                                        <div className='accordion-body small'>
                                            {section.renderContent()}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className='modal-footer'>
                        <button type='button' className='btn btn-secondary' data-bs-dismiss='modal'>Fechar</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OnboardingGuideModal;
